import { ChangeEvent, RefObject, useState } from 'react'

type ValidationRule = {
  required?: boolean
  requiredMsg?: string
  pattern?: RegExp
  patternMsg?: string
  minLength?: number
  minLengthMsg?: string
  maxLength?: number
  maxLengthMsg?: string
  fn?: (value: string) => boolean
  fnMessage?: string
}

type FieldValidationRule = ValidationRule & {
  dynamicValidation?: ValidationRule
}

export type ValidationRules = {
  [key: string]: FieldValidationRule
}

export type FormErrors = {
  [key: string]: string
}

type UseFormOptions<T> = {
  initialValues: T
  validationRules?: ValidationRules
  formRef?: RefObject<HTMLFormElement | null>
}

export const useForm = <T extends Record<string, any>>({
  initialValues,
  validationRules,
  formRef,
}: UseFormOptions<T>) => {
  const [values, setValues] = useState<T>(initialValues)
  const [errors, setErrors] = useState<FormErrors>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setValues((prev) => ({ ...prev, [name]: value }))

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }
  }

  const handleDynamicChange = (
    e: ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const fieldName = e.target.name

    setValues((prev) => ({
      ...prev,
      [fieldName]: prev[fieldName].map((item: string, i: number) =>
        i === index ? e.target.value : item
      ),
    }))

    const errorKey = `${String(fieldName)}.${index}`
    if (errors[errorKey]) {
      setErrors((prev) => ({
        ...prev,
        [errorKey]: '',
      }))
    }
  }

  const addDynamicField = (fieldName: keyof T, defaultValue = '') => {
    setValues((prev) => ({
      ...prev,
      [fieldName]: [...prev[fieldName], defaultValue],
    }))

    setTimeout(() => {
      if (formRef?.current) {
        const fieldEls = formRef.current.querySelectorAll(
          `input[name="${fieldName as string}"]`
        )
        if (fieldEls.length) {
          ;(fieldEls[fieldEls.length - 1] as HTMLInputElement).focus()
        }
      }
    }, 10)
  }

  const removeDynamicField = (fieldName: keyof T, index: number) => {
    setValues((prev) => ({
      ...prev,
      [fieldName]: prev[fieldName].filter(
        (_: string, i: number) => i !== index
      ),
    }))
    const indexedField = `${String(fieldName)}.${index}`
    if (errors[indexedField]) {
      setErrors((prev) => ({ ...prev, [indexedField]: '' }))
    }
  }

  const setFieldValue = <K extends keyof T>(field: K, value: T[K]) => {
    setValues((prev) => ({ ...prev, [field]: value }))
  }

  const _validate = (validationRules: ValidationRules): boolean => {
    const newErrors: FormErrors = {}

    for (let i = 0; i < Object.keys(validationRules).length; i++) {
      const field = Object.keys(validationRules)[i]
      const rule = validationRules[field]
      const value = values[field as keyof T]

      // Handle static fields
      if (
        rule.required &&
        (!value || (typeof value === 'string' && value.trim() === ''))
      ) {
        newErrors[field] = rule.requiredMsg ?? 'Field is required'
      } else if (rule.minLength && value && value.length < rule.minLength) {
        newErrors[field] = rule.minLengthMsg ?? 'Invalid'
      } else if (rule.maxLength && value && value.length > rule.maxLength) {
        newErrors[field] = rule.maxLengthMsg ?? 'Invalid'
      } else if (
        rule.pattern &&
        value &&
        typeof value === 'string' &&
        !rule.pattern.test(value)
      ) {
        newErrors[field] = rule.patternMsg ?? 'Invalid'
      } else if (rule.fn && value && !rule.fn(value)) {
        newErrors[field] = rule.fnMessage ?? 'Invalid'
      }

      // Focus input field
      if (
        Object.keys(newErrors).length !== 0 &&
        formRef?.current &&
        !rule.dynamicValidation
      ) {
        const fieldEl = formRef.current.querySelector(`input[name="${field}"]`)
        if (fieldEl) {
          ;(fieldEl as HTMLInputElement).focus()
        }
        setErrors(newErrors)
        return false
      }

      // Handle dynamic fields (arrays)
      if (rule.dynamicValidation && Array.isArray(value)) {
        for (let j = 0; j < value.length; j++) {
          const item = value[j]

          const dynamicRule = rule.dynamicValidation!
          const errorKey = `${field}.${j}`

          if (dynamicRule.required && (!item || item.trim() === '')) {
            newErrors[errorKey] = dynamicRule.requiredMsg ?? 'Field is required'
          } else if (
            dynamicRule.pattern &&
            item &&
            !dynamicRule.pattern.test(item)
          ) {
            newErrors[errorKey] = dynamicRule.patternMsg ?? 'Invalid'
          } else if (dynamicRule.fn && item && !dynamicRule.fn(item)) {
            newErrors[errorKey] = dynamicRule.fnMessage ?? 'Invalid'
          }

          // Focus input field
          if (Object.keys(newErrors).length !== 0 && formRef?.current) {
            const fieldEls = formRef.current.querySelectorAll(
              `input[name="${field}"]`
            )
            if (fieldEls.length) {
              ;(fieldEls[j] as HTMLInputElement).focus()
            }
            setErrors(newErrors)
            return false
          }
        }
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit =
    (
      onValid: (values: T) => Promise<void> | void
    ): React.FormEventHandler<HTMLFormElement> =>
    async (e) => {
      e.preventDefault()

      const isValid =
        !validationRules || (validationRules && _validate(validationRules))

      if (!isValid) {
        return
      }

      setIsSubmitting(true)
      try {
        await onValid(values)
      } finally {
        setIsSubmitting(false)
      }
    }

  return {
    values,
    errors,
    isSubmitting,
    handleChange,
    handleDynamicChange,
    addDynamicField,
    removeDynamicField,
    handleSubmit,
    setValues,
    setFieldValue,
  }
}
